import { useMemo } from 'react';
import { useUpstreamAccountsList } from '../../api/useUpstreamAccounts.ts';
import { useEgressProfiles } from '../../api/useEgressProfiles.ts';
import { useKeyBucketsList } from '../../api/useKeyBuckets.ts';
import { useKeyChannelBindingsIndex } from '../../api/useKeyChannelBindings.ts';
import { useUpstreamCapabilities, useUpstreamChannels } from '../../api/useUpstreams.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Chip, Panel, Skeleton } from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import { coerceErrorMessage } from '../Toast.tsx';
import { UPSTREAM_MODEL_SKU_SECTION_ID } from '../upstream-model-skus/UpstreamModelSkuTable.tsx';
import type { SupplyAccessDrawerId } from './SupplyAccessDrawers.tsx';
import { deriveSupplyAccessRows } from './supplyAccessReadiness.ts';
import type {
  SupplyAccessFact,
  SupplyAccessFactId,
  SupplyAccessRow,
  SupplyAccessState,
} from './supplyAccessReadiness.ts';

/**
 * Supply access — section 1 of the upstream detail page (epic #1327 PR-6).
 *
 * The page's two sections split the supply domain by grain: this one is per
 * upstream namespace (can it carry traffic at all?), the model SKU table
 * below is per model (what does this namespace expose, and at what cost?).
 *
 * Every readiness fact is derived from data the page already loads — there is
 * no readiness endpoint here on purpose. A server-side aggregate would be a
 * second opinion about the same rows the drawers write, free to disagree with
 * them the moment either side moves; deriving locally means the row an
 * operator fixes and the row they just read are the same row.
 *
 * Unresolved facts are click-through, and each one opens the drawer that owns
 * the fix rather than describing where to go. `unknown` is deliberately not
 * click-through: we do not know there is anything to fix.
 */

/**
 * Where an unresolved fact is fixed. Three of the four open a drawer; the
 * probe fact is cleared by protocol classification, which is a control in
 * section 2 on this same page — sending an operator to a drawer for it would
 * name a destination that cannot fix their blocker.
 */
export type SupplyAccessTarget =
  | { readonly kind: 'drawer'; readonly drawer: SupplyAccessDrawerId }
  | { readonly kind: 'anchor'; readonly id: string };

const FACT_TARGET: Record<SupplyAccessFactId, SupplyAccessTarget> = {
  account: { kind: 'drawer', drawer: 'accounts' },
  key: { kind: 'drawer', drawer: 'accounts' },
  egress: { kind: 'drawer', drawer: 'accounts' },
  probe: { kind: 'anchor', id: UPSTREAM_MODEL_SKU_SECTION_ID },
};

const FACT_LABEL: Record<SupplyAccessFactId, I18nKey> = {
  account: 'upstream_supply_access.fact.account',
  key: 'upstream_supply_access.fact.key',
  egress: 'upstream_supply_access.fact.egress',
  probe: 'upstream_supply_access.fact.probe',
};

const FACT_READY_HINT: Record<SupplyAccessFactId, I18nKey> = {
  account: 'upstream_supply_access.fact.account.ready',
  key: 'upstream_supply_access.fact.key.ready',
  egress: 'upstream_supply_access.fact.egress.ready',
  probe: 'upstream_supply_access.fact.probe.ready',
};

const FACT_BLOCKED_HINT: Record<SupplyAccessFactId, I18nKey> = {
  account: 'upstream_supply_access.fact.account.blocked',
  key: 'upstream_supply_access.fact.key.blocked',
  egress: 'upstream_supply_access.fact.egress.blocked',
  probe: 'upstream_supply_access.fact.probe.blocked',
};

const STATE_LABEL: Record<SupplyAccessState, I18nKey> = {
  ready: 'upstream_supply_access.state.ready',
  blocked: 'upstream_supply_access.state.blocked',
  unknown: 'upstream_supply_access.state.unknown',
};

const STATE_TONE: Record<SupplyAccessState, ChipTone> = {
  ready: 'ok',
  blocked: 'warn',
  unknown: 'neutral',
};

function FactCell({
  channelId,
  fact,
  onResolve,
}: {
  readonly channelId: string;
  readonly fact: SupplyAccessFact;
  readonly onResolve: (target: SupplyAccessTarget) => void;
}): JSX.Element {
  const t = useT();
  const label = t(FACT_LABEL[fact.id]);
  const hint = fact.state === 'unknown'
    ? t('upstream_supply_access.fact.unknown')
    : t(fact.state === 'ready' ? FACT_READY_HINT[fact.id] : FACT_BLOCKED_HINT[fact.id]);
  const chip = (
    <Chip tone={STATE_TONE[fact.state]}>
      {t(STATE_LABEL[fact.state])}
    </Chip>
  );
  const body = (
    <>
      <span className="text-dense font-medium text-paper">{label}</span>
      {chip}
      <span className="w-full text-label text-paper-low">{hint}</span>
    </>
  );
  const shared = 'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-well px-3 py-2 text-left';
  const testId = `supply-access-fact-${channelId}-${fact.id}`;

  // Only a blocked fact is actionable, so only it becomes a button. A ready
  // or unknown fact rendered as one would promise a fix that does not exist
  // and would land in the tab order for every namespace on the page.
  // Neither branch carries an aria-label: the cell's own text already names
  // the fact, its state, and what is wrong, which is more than a label could
  // hold without repeating it.
  if (fact.state !== 'blocked') {
    return (
      <div className={shared} data-testid={testId} data-state={fact.state}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onResolve(FACT_TARGET[fact.id])}
      className={`${shared} transition-colors duration-120 hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40`}
      data-testid={testId}
      data-state={fact.state}
    >
      {body}
    </button>
  );
}

function NamespaceRow({
  row,
  onResolve,
}: {
  readonly row: SupplyAccessRow;
  readonly onResolve: (target: SupplyAccessTarget) => void;
}): JSX.Element {
  const t = useT();
  return (
    <li
      className="flex flex-col gap-3 border-b border-line-soft px-4 py-3 last:border-b-0"
      data-testid={`supply-access-row-${row.channelId}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-body text-paper">{row.channelSlug}</span>
        <span className="font-mono text-micro text-paper-low">{row.channelId}</span>
        {row.stale ? (
          <Chip tone="warn">{t('upstream_supply_access.namespace.stale')}</Chip>
        ) : row.channelEnabled ? null : (
          <Chip tone="neutral">{t('upstream_supply_access.namespace.disabled')}</Chip>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {row.facts.map((fact) => (
          <FactCell key={fact.id} channelId={row.channelId} fact={fact} onResolve={onResolve} />
        ))}
      </div>
    </li>
  );
}

export function SupplyAccessSection({
  upstreamId,
  onOpenDrawer,
}: {
  readonly upstreamId: string;
  readonly onOpenDrawer: (drawer: SupplyAccessDrawerId) => void;
}): JSX.Element {
  const t = useT();
  const channelsQ = useUpstreamChannels(upstreamId);
  const capabilitiesQ = useUpstreamCapabilities(upstreamId);
  const accountsQ = useUpstreamAccountsList(upstreamId);
  const keyBucketsQ = useKeyBucketsList(upstreamId);
  const egressProfilesQ = useEgressProfiles();

  const bucketIds = useMemo(
    () => (keyBucketsQ.data ?? []).map((bucket) => bucket.id),
    [keyBucketsQ.data],
  );
  const bindings = useKeyChannelBindingsIndex(upstreamId, bucketIds);
  // The binding fan-out is keyed off the bucket list, so an unloaded list
  // yields useQueries([]) — settled with zero entries. Folding the list's own
  // state in keeps "we have not asked yet" from reading as "this key reaches
  // no namespace", which would render a blocker that does not exist.
  const bindingsByBucketId = keyBucketsQ.data === undefined ? undefined : bindings.byBucketId;

  const rows = useMemo(
    () => deriveSupplyAccessRows({
      channels: channelsQ.data ?? [],
      capabilities: capabilitiesQ.data,
      accounts: accountsQ.data,
      keyBuckets: keyBucketsQ.data,
      bindingsByBucketId,
      egressProfiles: egressProfilesQ.data,
    }),
    [
      channelsQ.data,
      capabilitiesQ.data,
      accountsQ.data,
      keyBucketsQ.data,
      bindingsByBucketId,
      egressProfilesQ.data,
    ],
  );

  /**
   * Scrolling rather than routing for the anchor case: section 2 is already
   * on this page, so a router navigation would re-render the page to reach
   * something a few hundred pixels down. `scrollIntoView` is guarded because
   * happy-dom and older browsers do not implement it.
   */
  function resolveFact(target: SupplyAccessTarget): void {
    if (target.kind === 'drawer') {
      onOpenDrawer(target.drawer);
      return;
    }
    const node = document.getElementById(target.id);
    node?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }

  // No caption actions: the page header carries the drawer entries a few
  // pixels above, and the unresolved facts below are the in-context ones.
  // A third copy between them would be noise, not a shortcut.
  return (
    <Panel
      caption={t('upstream_supply_access.title')}
      testId="supply-access-section"
    >
      <p className="m-0 border-b border-line-soft px-4 py-2.5 text-dense text-paper-low">
        {t('upstream_supply_access.subtitle')}
      </p>
      {channelsQ.isLoading || capabilitiesQ.isLoading ? (
        <div className="flex flex-col gap-2 p-4" data-testid="supply-access-loading">
          {[1, 2].map((index) => <Skeleton key={index} className="h-16 w-full" />)}
        </div>
      ) : channelsQ.isError ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-b border-line-soft bg-bad-dim px-4 py-3 last:border-b-0"
          role="alert"
          data-testid="supply-access-error"
        >
          <span className="text-dense text-bad-bright">
            {t('upstream_supply_access.load_error', { message: coerceErrorMessage(channelsQ.error) })}
          </span>
          <Button size="sm" onClick={() => void channelsQ.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="m-0 px-4 py-8 text-center text-dense text-paper-low" data-testid="supply-access-empty">
          {t('upstream_supply_access.empty')}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0" data-testid="supply-access-rows">
          {rows.map((row) => (
            <NamespaceRow key={row.channelId} row={row} onResolve={resolveFact} />
          ))}
        </ul>
      )}
    </Panel>
  );
}
